import { css } from '@emotion/core';
import {
  EventDTO,
  EventType,
  SloPerformanceDTO,
  SloQualityResultDTO,
} from '@atlassiansox/phobos-client-ts';
import { token } from '@atlaskit/tokens';

import { colors, MEET_COLOR, BREACH_COLOR } from '@/styles';
import { assertUnreachable } from '@/utils';
import { DataPointsEventsTotals, DataPointWithEvents } from '@/ui';

import { SLOStatus, SLOQualityStatus } from '../expanded/types';

import { ERROR_BUDGET_DECIMAL_PLACES, SLO_DECIMAL_PLACES, SLI_DECIMAL_PLACES } from './constants';

export function truncateToDecimals(num: number, dec = 2) {
  const calcDec = Math.pow(10, dec);
  return Math.trunc(num * calcDec) / calcDec;
}

export function determineSloStatus({
  performance,
  objective,
}: {
  performance?: SloPerformanceDTO;
  objective?: number;
}): SLOStatus {
  if (performance && objective) {
    if (performance.result === undefined || performance.result === null) {
      return 'neither';
    }
    return performance.result * 100 > objective ? 'meets' : 'breaches';
  }

  return 'neither';
}

export function determineSloQualityStatus({
  quality,
}: {
  quality: SloQualityResultDTO;
}): SLOQualityStatus {
  if (quality) {
    if (!quality.resultItemsCount) {
      return 'neither';
    }
    return quality.qualityScore === 10 ? 'healthy' : 'unhealthy';
  }

  return 'neither';
}

export const getSloStatusColor = (status: SLOStatus): string =>
  ({
    meets: MEET_COLOR,
    breaches: BREACH_COLOR,
    neither: token('color.text.subtlest', colors.N200),
  }[status ?? 'neither']);

export const getSloQualityStatusColor = (status: SLOQualityStatus): string =>
  ({
    healthy: MEET_COLOR,
    unhealthy: BREACH_COLOR,
    neither: token('color.text.subtlest', colors.N200),
  }[status ?? 'neither']);

export type SLINumberProps = {
  performance?: SloPerformanceDTO;
  objective?: number;
  quality?: SloQualityResultDTO;
};

export const getHeroNumberColorStyles = ({ performance, objective, quality }: SLINumberProps) => {
  let color: string = 'inherit';

  if (performance) {
    const status: SLOStatus = determineSloStatus({ performance, objective });
    color = getSloStatusColor(status);
  }

  if (quality) {
    const qualityStatus: SLOQualityStatus = determineSloQualityStatus({ quality });
    color = getSloQualityStatusColor(qualityStatus);
  }

  return css`
    color: ${color};
  `;
};

interface FormatErrorBudgetOptionsParam {
  truncate: boolean;
}

export function formatErrorBudget(
  errorBudget: number | undefined,
  options: FormatErrorBudgetOptionsParam = { truncate: true },
  decimalPlaces: number = ERROR_BUDGET_DECIMAL_PLACES
): string {
  if (errorBudget === undefined) {
    return 'N/A';
  }

  const { truncate } = options;

  if (truncate) {
    if (errorBudget < -999) {
      return '< -999';
    }
  }

  return `${truncateToDecimals(errorBudget, decimalPlaces).toString()}`;
}

export function getErrorBudget(result: number | undefined, objective: number): number | undefined {
  if (result !== undefined && result !== null && objective) {
    const errorBudgetTotal = 100 - objective;
    const errorBudget = ((result * 100 - objective) / errorBudgetTotal) * 100;

    return errorBudget;
  }

  return undefined;
}

export function formatPerformanceResult(
  result: number | undefined,
  decimalPlaces: number = SLI_DECIMAL_PLACES
): string {
  if (result === undefined || result === null) return 'N/A';
  return truncateToDecimals(result * 100, decimalPlaces).toString();
}

export function formatQualityScoreResult(result: number | undefined): string {
  if (result === undefined || result === 0) return 'N/A';
  return result.toString();
}

export function formatObjectiveResult(result: number | undefined): string {
  if (result === undefined || result === null) return 'N/A';
  return truncateToDecimals(result, SLO_DECIMAL_PLACES).toString();
}

export function getBurnEventType(e: EventDTO) {
  switch (e.type) {
    case EventType.BurnGradualAnd:
    case EventType.SignalfxAlertBurnGradual:
      return 'Gradual';
    case EventType.BurnSteepAnd:
    case EventType.SignalfxAlertBurnSteep:
      return 'Steep';
    case EventType.SignalfxAlertBurnModerate:
      return 'Moderate';
    case EventType.SignalfxAlertBurnUltraSteep:
      return 'Ultra Steep (5-minute)';
    case EventType.BurnGradualOr:
    case EventType.BurnSteepOr:
    case EventType.SignalfxAlertBreach:
    case EventType.SignalfxAlertBurn:
    case EventType.Breach:
      return 'Unknown';
    default:
      return assertUnreachable(e.type);
  }
}

export function isBreach(e: EventDTO) {
  return e.type === EventType.Breach;
}

export function isBurn(e: EventDTO) {
  return e.type === EventType.BurnGradualAnd || e.type === EventType.BurnSteepAnd;
}

export function isSfxBreach(e: EventDTO) {
  return e.type === EventType.SignalfxAlertBreach;
}

export function isSfxBurn(e: EventDTO) {
  const burnEvents: EventType[] = [
    EventType.SignalfxAlertBurn,
    EventType.SignalfxAlertBurnGradual,
    EventType.SignalfxAlertBurnModerate,
    EventType.SignalfxAlertBurnSteep,
    EventType.SignalfxAlertBurnUltraSteep,
  ];
  return burnEvents.includes(e.type);
}

export const isLowTrafficEvent = (e: EventDTO) => e.lowTraffic;
export const isRegularTrafficEvent = (e: EventDTO) => !e.lowTraffic;

const toDataPointEventTotal = (d: DataPointWithEvents): DataPointsEventsTotals => ({
  ...d,
  totalCount: (d?.goodCount ?? 0) + (d?.badCount ?? 0),
});

export const mapToDataPointEventTotals = (dps: DataPointWithEvents[]): DataPointsEventsTotals[] =>
  dps.map(toDataPointEventTotal);

export const populateDailyDataTotals = (
  errorBudgetData: DataPointWithEvents[],
  dailyData: DataPointWithEvents[]
): DataPointsEventsTotals[] =>
  errorBudgetData.map((e, i) => {
    const { goodCount, badCount, totalCount } = toDataPointEventTotal(dailyData[i]);
    return {
      ...e,
      dailyGood: goodCount,
      dailyBad: badCount,
      totalCount,
    };
  });
